import { ReactNode } from "react";
import { AssetType } from "@zapier/canvas-state";

import { ChatMock } from "./previews/ChatMock";
import { InterfaceMock } from "./previews/InterfaceMock";
import { TableMock } from "./previews/TableMock";
import { ZapMock } from "./previews/ZapMock";
import { AgentThumbnail } from "./previews/AgentThumbnail";
import {
  UnlinkedChatbot,
  UnlinkedInterface,
  UnlinkedTable,
  UnlinkedZap,
} from "./previews/Unlinked";
import { AssetPreviewWrapper } from "./AssetPreviewWrapper";
import styles from "./AssetPreview.module.css";

type Props = {
  assetType: AssetType;
  id: string;
  assetId?: string | undefined | null;
  showAssetDetailsButton?: boolean;
  projectId?: string | undefined;
};

type AssetWrapperProps = {
  children: ReactNode;
};

function Wrapper({ children }: AssetWrapperProps) {
  return <div className={styles.root}>{children}</div>;
}

/* Agent nodes don't have a mock SVG since we show the step title instead */
function getUnlinkedChildren(assetType: Props["assetType"]) {
  switch (assetType) {
    case "table":
      return (
        <Wrapper>
          <UnlinkedTable />
        </Wrapper>
      );
    case "zap":
      return (
        <Wrapper>
          <UnlinkedZap />
        </Wrapper>
      );
    case "interface":
      return (
        <Wrapper>
          <UnlinkedInterface />
        </Wrapper>
      );
    case "chatbot":
      return (
        <Wrapper>
          <UnlinkedChatbot />
        </Wrapper>
      );

    default:
      return null;
  }
}

function getChildren(
  assetType: Props["assetType"],
  assetId: string | undefined | null,
) {
  if (!assetId) {
    return getUnlinkedChildren(assetType);
  }

  switch (assetType) {
    case "table":
      return (
        <Wrapper>
          <TableMock />
        </Wrapper>
      );
    case "zap":
      return (
        <Wrapper>
          <ZapMock />
        </Wrapper>
      );
    case "interface":
      return (
        <Wrapper>
          <InterfaceMock />
        </Wrapper>
      );
    case "chatbot":
      return (
        <Wrapper>
          <ChatMock />
        </Wrapper>
      );
    case "agent":
      return <AgentThumbnail agentId={assetId!} />;

    default:
      return null;
  }
}

export const AssetPreview = ({
  id,
  assetType,
  assetId,
  showAssetDetailsButton = false,
  projectId,
}: Props) => {
  return (
    <AssetPreviewWrapper
      id={id}
      assetType={assetType}
      assetId={assetId}
      showAssetDetailsButton={showAssetDetailsButton}
      projectId={projectId}
    >
      {getChildren(assetType, assetId)}
    </AssetPreviewWrapper>
  );
};
